import type { LogEntry, MetricEntry, UnifiedPayload, RequestLike } from '../core/MonitoringClient'
import { MonitoringClient, LogLevel } from '../core/MonitoringClient'
import { LogFormatter } from '../formatters/LogFormatter'
import { MetricValidator } from '../validators/MetricValidator'
import { PayloadProcessor } from '../formatters/PayloadProcessor'
import { Sampler } from '../utils/Sampler'
import { CircuitBreaker } from '../utils/CircuitBreaker'
import debug from 'debug'

const log = debug('s4l:monitoring:browserClient')

export class BrowserMonitoringClient extends MonitoringClient {
  private logBuffer: LogEntry[] = []
  private metricBuffer: MetricEntry[] = []
  private sessionId: string
  private endpoint: string
  // private maxBatchSize: number
  private flushInterval: number
  private maxPayloadSize: number
  private flushTimer: number | null = null
  private logFormatter: LogFormatter
  private metricValidator: MetricValidator
  private payloadProcessor: PayloadProcessor
  private sampler: Sampler
  private metricSampler: Sampler
  private circuitBreaker: CircuitBreaker

  static create(
    config: {
      endpoint?: string
      // maxBatchSize?: number
      flushInterval?: number
      maxPayloadSize?: number
      logFormatter?: LogFormatter
      metricValidator?: MetricValidator
      payloadProcessor?: PayloadProcessor
      sampler?: Sampler
      metricSampler?: Sampler
      circuitBreaker?: CircuitBreaker
    } = {}
  ) {
    return new BrowserMonitoringClient(config)
  }

  constructor(
    config: {
      endpoint?: string
      // maxBatchSize?: number
      flushInterval?: number
      maxPayloadSize?: number
      logFormatter?: LogFormatter
      metricValidator?: MetricValidator
      payloadProcessor?: PayloadProcessor
      sampler?: Sampler
      metricSampler?: Sampler
      circuitBreaker?: CircuitBreaker
    } = {}
  ) {
    super()

    this.endpoint = config.endpoint || '/api/logs'
    // this.maxBatchSize = config.maxBatchSize || 50
    this.flushInterval = config.flushInterval || 10000
    this.maxPayloadSize = config.maxPayloadSize || 60000
    this.sessionId = this.generateSessionId()

    // Initialize dependencies
    this.logFormatter = config.logFormatter ?? LogFormatter.create()
    this.metricValidator = config.metricValidator ?? MetricValidator.create()
    this.payloadProcessor = config.payloadProcessor ?? PayloadProcessor.create()
    this.sampler = config.sampler ?? Sampler.create()
    this.metricSampler = config.metricSampler ?? this.sampler ?? Sampler.create()
    this.circuitBreaker = config.circuitBreaker ?? CircuitBreaker.create()

    this.initializeUnloadStrategy()
    this.startPeriodicFlush()
  }

  info({
    message,
    metadata,
  }: {
    message: string
    metadata?: Record<string, unknown>
    request?: RequestLike
  }): void {
    if (!this.circuitBreaker.shouldAcceptEvent()) {
      return
    }

    this.log(LogLevel.INFO, message, metadata)
  }

  warn({
    message,
    metadata,
  }: {
    message: string
    metadata?: Record<string, unknown>
    request?: RequestLike
  }): void {
    if (!this.circuitBreaker.shouldAcceptEvent()) {
      return
    }

    this.log(LogLevel.WARN, message, metadata)
  }

  error({
    message,
    metadata,
  }: {
    message: string
    metadata?: Record<string, unknown>
    request?: RequestLike
  }): void {
    if (!this.circuitBreaker.shouldAcceptEvent()) {
      return
    }

    this.log(LogLevel.ERROR, message, metadata)
  }

  debug({
    message,
    metadata,
  }: {
    message: string
    metadata?: Record<string, unknown>
    request?: RequestLike
  }): void {
    if (!this.circuitBreaker.shouldAcceptEvent()) {
      return
    }

    this.log(LogLevel.DEBUG, message, metadata)
  }

  sendMetric(metric: MetricEntry): void {
    if (!this.circuitBreaker.shouldAcceptEvent()) {
      return
    }

    if (!this.metricSampler.shouldSample()) {
      return
    }

    if (!this.metricValidator.isValidBasic(metric)) {
      log('[METRIC_VALIDATION_ERROR] Invalid metric ignored:', metric)
      return
    }

    const sanitizedMetric = this.metricValidator.sanitizeMetric({
      ...metric,
      metadata: {
        ...(metric.metadata ?? {}),
        origin: (metric.metadata as any)?.origin ?? 'browser',
      },
    } as MetricEntry)

    this.metricBuffer.push(sanitizedMetric)
  }

  private log(level: LogLevel, message: string, metadata?: Record<string, unknown>): void {
    // Format and sanitize the message
    const formattedMessage = this.logFormatter.formatMessage(message)
    const sanitizedMessage = this.logFormatter.sanitizeMessage(formattedMessage)

    // Format metadata if provided
    const formattedMetadata = metadata ? this.logFormatter.formatMetadata(metadata) : undefined

    const logEntry: LogEntry = {
      timestamp: Date.now(),
      level,
      message: sanitizedMessage,
      metadata: formattedMetadata,
      sessionId: this.sessionId,
      url: window.location.href,
      userAgent: navigator.userAgent,
      origin: 'browser',
    }

    this.logBuffer.push(logEntry)

    // this.maxBatchSizeReached() && this.flush() // eslint-disable-line
  }

  // private maxBatchSizeReached(): boolean {
  //   return this.logBuffer.length + this.metricBuffer.length >= this.maxBatchSize
  // }

  private flush(): Promise<boolean> {
    if (this.circuitBreaker.isOpen()) {
      // Clear buffers to prevent memory leak
      this.logBuffer = []
      this.metricBuffer = []
      return Promise.resolve(false)
    }

    const payload: UnifiedPayload = {
      logs: this.logBuffer.length > 0 ? [...this.logBuffer] : undefined,
      metrics: this.metricBuffer.length > 0 ? [...this.metricBuffer] : undefined,
    }

    // Clear buffers
    this.logBuffer = []
    this.metricBuffer = []

    return this.sendPayload(payload)
  }

  private async sendPayload(payload: UnifiedPayload): Promise<boolean> {
    // Process payload: optimize, compress, and chunk if needed
    const processed = this.payloadProcessor.process(payload, this.maxPayloadSize, {
      removeWhitespace: true,
      abbreviateKeys: false,
      maxStringLength: 2000,
    })

    // Send the main payload
    const mainPromise = this.sendBeacon(processed.payload)

    // If there are additional chunks, send them too
    if (processed.chunks && processed.chunks.length > 0) {
      const chunkPromises = processed.chunks.map(chunk => this.sendBeacon(chunk))

      // Return true if at least the main payload succeeds (chunks are best-effort)
      return mainPromise.then(mainSuccess => {
        if (mainSuccess) {
          // Send chunks in background without waiting
          Promise.allSettled(chunkPromises).then(results => {
            const failedChunks = results.filter(r => r.status === 'rejected').length
            if (failedChunks > 0) {
              log(`Failed to send ${failedChunks} payload chunks`)
            }
          })
          return true
        }
        return false
      })
    }

    return mainPromise
  }

  private async sendBeacon(payload: string): Promise<boolean> {
    if (!navigator.sendBeacon) {
      return false
    }

    try {
      const blob = new Blob([payload], {
        type: 'text/plain',
      })
      return navigator.sendBeacon(this.endpoint, blob)
    } catch {
      return false
    }
  }

  private generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substring(2)
  }

  private initializeUnloadStrategy(): void {
    let sentUnloadBeacon = false

    const sendUnloadBeacon = () => {
      if (sentUnloadBeacon) return
      sentUnloadBeacon = true

      if (
        !this.circuitBreaker.isOpen() &&
        this.sampler.shouldSample() &&
        (this.logBuffer.length > 0 || this.metricBuffer.length > 0)
      ) {
        this.flush()
      }
    }

    // Use pagehide + visibilitychange strategy (doesn't break BFCache)
    if (typeof window !== 'undefined') {
      if ('onpagehide' in window) {
        window.addEventListener('pagehide', sendUnloadBeacon, { capture: true })
      } else {
        // Fallback for older browsers
        const win = window as Window & typeof globalThis
        win.addEventListener('unload', sendUnloadBeacon, { capture: true })
        win.addEventListener('beforeunload', sendUnloadBeacon, { capture: true })
      }
    }

    if (typeof document !== 'undefined') {
      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.visibilityState === 'hidden') {
            sendUnloadBeacon()
          }
        },
        { capture: true }
      )
    }
  }

  private startPeriodicFlush(): void {
    const self = this // eslint-disable-line
    self.flushTimer = window.setInterval(() => {
      if (
        !self.circuitBreaker.isOpen() &&
        this.sampler.shouldSample() &&
        (self.logBuffer.length > 0 || self.metricBuffer.length > 0)
      ) {
        this.flush().catch(error => {
          log('Error in periodic flush:', error)
        })
      }
    }, self.flushInterval)
  }
}
